import { produce } from 'immer'
import { convertToRefString } from 'lib/websocket-notify'
import {
    ERRORED,
    INVALIDATE_TAG,
    INVALIDATE_TAGS,
    LOADED,
    LOADING,
    MARK_DIRTY,
    MODIFY_LIST,
    RESET_LIST,
    RESET_LIST_LOADING,
    SUBSCRIBE_LIST,
    SUBSCRIBE_OBJECT,
    UNSUBSCRIBE,
} from './actionTypes'

export type SerializedErrorType = {
    message: string
    name: string
    code?: string
}

export const serializedError = (e: Error | unknown): SerializedErrorType => {
    const error: Error = e instanceof Error ? e : new Error(`unknown error: ${e}`)
    const s: SerializedErrorType = {
        message: error.message,
        name: error.name,
    }

    if ('code' in error && typeof error.code === 'string') {
        s.code = error.code
    }
    return s
}

export type ReduxObjectCache<Data> = {
    readonly isLoading: boolean
    readonly isBackgroundLoading: boolean
    readonly isError: boolean
    readonly data?: Readonly<Data>
    readonly error?: Readonly<SerializedErrorType>
    readonly tag?: string
}

export type ReduxListCache<Data> = Readonly<
    Omit<ReduxObjectCache<Data>, 'data'> & {
        data: ReadonlyArray<Data>
        canLoadMore: boolean
        shouldInvalidateList: boolean
        metadata: {
            readonly tag?: string
            readonly tags?: Record<string, Array<number>>
        }
        lastPollTimeForRef: Record<string, number>
    }
>

export const grpcReducerNamespace = 'toolkit' as const

export type ReduxObjectState<Data> = Record<typeof grpcReducerNamespace, Record<string, Record<string, ReduxObjectCache<Data>>>>
export type ReduxListState<Data> = Record<typeof grpcReducerNamespace, Record<string, Record<string, ReduxListCache<Data>>>>
export type ReduxState<Data> = Record<typeof grpcReducerNamespace,
    Record<string, Record<string, ReduxListCache<Data> | ReduxObjectCache<Data>>>>

export const grpcReducer = produce(
    (draft, { type, reducer, cacheKey, error, response, status, opts, isLoading, ...rest }) => {
        const data: ReduxListCache<unknown> = {
            isLoading: true,
            isBackgroundLoading: false,
            isError: false,
            data: [],
            error: undefined,
            // opts,
            canLoadMore: true,
            shouldInvalidateList: false,
            metadata: {
                tag: rest.tag,
                tags: {},
            },
            lastPollTimeForRef: {},
        }

        if (type === SUBSCRIBE_LIST) {
            if (!draft[reducer]) {
                draft[reducer] = { [cacheKey]: data }
                return
            }
            if (!draft[reducer][cacheKey]) {
                draft[reducer][cacheKey] = data
            }
            return
        }

        if (type === SUBSCRIBE_OBJECT) {
            const anotherData = {
                isLoading: true,
                isBackgroundLoading: false,
                isError: false,
                data: undefined,
                error: undefined,
                tag: rest.tag,
                opts,
            }

            if (!draft[reducer]) {
                draft[reducer] = { [cacheKey]: anotherData }
                return
            }
            if (!draft[reducer][cacheKey]) {
                draft[reducer][cacheKey] = anotherData
            }

            return
        }

        const cache = draft[reducer]?.[cacheKey]
        if (!cache && reducer) {
            console.warn('no cache', draft[reducer], draft, reducer, cacheKey)
            return
        }
        switch (type) {
            case UNSUBSCRIBE:
                draft[reducer] && delete draft[reducer][cacheKey]
                return

            case RESET_LIST_LOADING:
                cache.isLoading = isLoading
                cache.isBackgroundLoading = cache.data && cache.data.length > 0
                cache.isError = false
                cache.error = undefined
                cache.canLoadMore = true
                cache.shouldInvalidateList = false
                cache.metadata = {
                    tag: rest.tag,
                    tags: {},
                }
                return

            case RESET_LIST:
                cache.isLoading = false
                cache.isBackgroundLoading = false
                cache.isError = false
                cache.error = error
                cache.status = status
                cache.canLoadMore = rest.canLoadMore
                cache.shouldInvalidateList = false

                if (!cache.metadata.tag) {
                    cache.data = []
                    cache.data[rest.page] = response
                    return
                }

                response.forEach?.((r, i: number) => {
                    if (!r) {
                        return
                    }

                    const refStringFromCache = convertToRefString(r, cache.metadata.tag)
                    if (!refStringFromCache) {
                        console.warn('unable to convert data to ref string')
                        return
                    }
                    cache.metadata.tags[refStringFromCache] = [rest.page, i]
                })
                cache.data = []
                cache.data[rest.page] = response
                return

            case LOADING:
                if (cache.data && (Array.isArray(cache.data) ? cache.data.length > 0 : true)) {
                    cache.isBackgroundLoading = true
                    cache.isLoading = false
                } else {
                    cache.isLoading = true
                    cache.isBackgroundLoading = false
                }
                return

            case LOADED:
                cache.isLoading = false
                cache.isBackgroundLoading = false
                cache.isError = false
                // TODO: step through objects!
                cache.data = response
                cache.status = status
                if (rest.tag) {
                    cache.tag = convertToRefString(response, rest.tag)
                }
                return

            case ERRORED:
                cache.isLoading = false
                cache.isBackgroundLoading = false
                cache.isError = true
                cache.error = error
                cache.status = status
                cache.input = response
                return

            case MODIFY_LIST:
                cache.isLoading = isLoading
                cache.isBackgroundLoading = false
                cache.isError = false
                cache.error = error
                cache.status = status
                cache.canLoadMore = rest.canLoadMore
                cache.shouldInvalidateList = false
                if (!cache.metadata.tag) {
                    cache.data[rest.page] = response
                    return
                }

                response.forEach?.((r, i: number) => {
                    if (!r) {
                        return
                    }

                    const refStringFromCache = convertToRefString(r, cache.metadata.tag)
                    if (!refStringFromCache) {
                        console.warn('unable to convert data to ref string')
                        return
                    }
                    cache.metadata.tags[refStringFromCache] = [rest.page, i]
                })
                cache.data[rest.page] = response
                return

            case INVALIDATE_TAGS: {
                if (rest.refStrings.length !== response.length) {
                    console.warn('did not receive the same number of tags as ref strings')
                    // Return early to avoid mutating the cache?
                    return
                }

                const tags = cache.metadata.tags
                if (!tags) {
                    console.warn('no tag metadata')
                    return
                }

                for (const [i, refString] of rest.refStrings.entries()) {
                    const [x, y] = tags[refString] || []

                    if (!isFinite(x) || x > cache.data.length) {
                        console.warn('invalid item page in cache')
                        continue
                    }
                    if (!isFinite(y) || y > cache.data[x].length) {
                        console.warn('invalid item location in cached page')
                        continue
                    }

                    const cachedItem = cache.data[x][y]
                    const refStringFromCache = convertToRefString(cachedItem, cache.metadata.tag)

                    if (!refStringFromCache) {
                        continue
                    }
                    if (refStringFromCache !== refString) {
                        console.warn("tag doesn't match ref of the item to update")
                        continue
                    }

                    cache.data[x][y] = response[i]
                }
                cache.lastPollTimeForRef = rest.lastPollTimeForRef
                break
            }

            case INVALIDATE_TAG: {
                const taggedId = rest.refString

                if (cache.tag) {
                    const refStringFromCache = convertToRefString(cache.data, cache.tag)
                    if (refStringFromCache !== taggedId) {
                        console.warn("Invalidating tag: tag doesn't match ref of the item to update")
                        return
                    }
                    cache.data = response
                    return
                }

                const tags = cache.metadata.tags
                if (!tags) {
                    console.warn('Invalidating tag: no tag metadata')
                    return
                }

                const [x, y] = tags[rest.refString] || []

                if (!isFinite(x) || x > cache.data.length) {
                    console.warn('Invalidating tag: invalid item page in cache')
                    return
                }
                if (!isFinite(y) || y > cache.data[x].length) {
                    console.warn('Invalidating tag: invalid item location in cached page')
                    return
                }

                const cachedItem = cache.data[x][y]
                const refStringFromCache = convertToRefString(cachedItem, cache.metadata.tag)

                if (!refStringFromCache) {
                    return
                }
                if (refStringFromCache !== rest.refString) {
                    console.warn("Invalidating tag: tag doesn't match ref of the item to update")
                    return
                }

                if (refStringFromCache !== convertToRefString(response, cache.metadata.tag)) {
                    console.warn("Invalidating tag: response tag doesn't match ref of the item to update")
                    return
                }

                cache.data[x][y] = response
                return
            }

            case MARK_DIRTY:
                cache.shouldInvalidateList = true
                break

            // no default
        }
    },
    {},
)
